import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Hook ligero para contar las anomalías ACTIVAS de severidad CRÍTICA del módulo
 * de Inventario. Pensado para los badges del sidebar y de la pestaña Análisis IA.
 *
 * Características:
 *   - Solo subscribe cuando `enabled` es true (típicamente cuando el restaurante
 *     tiene el módulo de inventario activado). Si no, desuscribe y devuelve 0
 *     sin abrir conexiones a Firestore.
 *   - Filtra por `status === 'active'` en la query (1 sola condición → no requiere
 *     índice compuesto). El filtro `severity === 'critical'` corre en memoria,
 *     barato porque las anomalías activas suelen ser ≤ 20 docs.
 *   - Devuelve un número plano. La UI decide cómo mostrarlo (chip, dot, etc).
 *
 * @param {string|null} restaurantId
 * @param {boolean} enabled — gating; si false, no subscribe.
 * @returns {number} count de anomalías activas + críticas.
 */
export function useCriticalAnomaliesCount(restaurantId, enabled) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!restaurantId || !enabled) {
      setCount(0);
      return undefined;
    }

    const q = query(
      collection(db, 'restaurants', restaurantId, 'inventoryAnomalies'),
      where('status', '==', 'active')
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        let critical = 0;
        snap.forEach((doc) => {
          if (doc.data()?.severity === 'critical') critical += 1;
        });
        setCount(critical);
      },
      (error) => {
        // Si el restaurante todavía no tiene módulo o reglas de seguridad bloquean,
        // mostramos 0 silenciosamente para no romper el sidebar.
        console.warn('[useCriticalAnomaliesCount] subscription error:', error?.message || error);
        setCount(0);
      }
    );

    return () => unsubscribe();
  }, [restaurantId, enabled]);

  return count;
}
